import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Filters, LegacyCard, Stack, Text, Thumbnail, Tooltip, Pagination, Spinner } from "@shopify/polaris";
import { DeleteMinor, ImageMajor, ImagesMajor } from "@shopify/polaris-icons";
import TargetedProductsTable from "../../../../../../../../../common/TargetedProductsTable/TargetedProductsTable.js";
import AddItemsButtons from "../AddItemsButtons.js";
import { cutTitle } from "../../../../../../../../../../utils/cutTitle.js";
import { openModal } from "../../../../../../../../../../store/features/modal.js";
import { OPEN_MODAL_COLLECTION } from "../../../../../../../../../../constants/modalTypes.js";
import { useAppDispatch } from "../../../../../../../../../../hooks/redux.js";
import { useAppMutation } from "../../../../../../../../../../hooks/useAppMutation.js";

const getItemImage = (item) => {
  if (item.featuredImage) {
    return item.featuredImage;
  }
  if (item.image) {
    return item.image.originalSrc;
  }
  if (item.images && item.images[0] && item.images[0].originalSrc) {
    return item.images[0].originalSrc;
  }
  if (item.type === 'collection') {
    return ImagesMajor;
  }
  return ImageMajor;
};

const getStockQuantityColumn = (item) => {
  if (item.id.includes("Collection")) {
    return "-";
  }
  if (!item.tracksInventory) {
    return "Inventory not tracked";
  }
  return item.totalInventory;
};

const getProductsColumn = (item) => {
  if (item.id.includes("Collection")) {
    return item.productsCount;
  }
  return 1;
};

const tableHeadings = [
  "#",
  "Product/Collection",
  // "Stock Quantity",
  "Products",
  ""
];

const tableColumnContentTypes = [
  "numeric",
  "text",
  // "numeric",
  "numeric",
  "text"
];

const SectionWithTable = ({ values, filterValues, handleAddProducts, handleAddCollections, handleDelete, initialSelectionIds, input, totalCount, totalFetched, setTotalFetched, setPage, page, handleRemoveProducts }) => {
  const [selectedItems, setSelectedItems] = useState(values.selectedSpecificItems || []);
  const [filteredItems, setFilteredItems] = useState(values.selectedSpecificItems || []);
  const dispatch = useAppDispatch();
  const [queryValue, setQueryValue] = useState("");
  const limit = 50;

  const { mutate: loadProducts, isLoading: isLoadingProducts } = useAppMutation({
    url: "/api/products/filters",
    reactMutationOptions: {
      onSuccess(data) {
        const newProducts = [...selectedItems, ...data.products];
        setSelectedItems(newProducts);
        setFilteredItems(newProducts);
        input.onChange(newProducts);
        setTotalFetched(prev => prev + data.products.length);
      }
    }
  });

  const fetchProducts = useCallback((skip = 0, take = limit) => {
    loadProducts({
      ...filterValues,
      skip,
      take,
    });
  }, [loadProducts, filterValues, limit]);

  const handleNextPage = useCallback(() => {
    const newPage = page + 1;
    const startIdx = (newPage - 1) * limit;
    const endIdx = newPage * limit;

    const pageProducts = values.selectedSpecificItems.slice(startIdx, endIdx);

    if (pageProducts.length > 0 && (pageProducts.length === limit || endIdx >= totalCount)) {
      setPage(newPage);
    } else {
      setPage(newPage);
      fetchProducts(totalFetched, limit);
    }
  }, [page, fetchProducts, values.selectedSpecificItems, limit, totalCount, totalFetched]);

  const handlePrevPage = useCallback(() => {
    if (page > 1) {
      const newPage = page - 1;
      setPage(newPage);
    }
  }, [page]);

  const handleQueryValueRemove = useCallback(() => {
    setQueryValue("");
  }, []);

  const handleClearAll = useCallback(() => {
    handleQueryValueRemove();
  }, [handleQueryValueRemove]);

  const handleClickToCollection = useCallback((item) => {
    dispatch(openModal({ modalType: OPEN_MODAL_COLLECTION, modalData: { collection: item } }));
  }, [dispatch]);

  useEffect(() => {
    setSelectedItems(values.selectedSpecificItems);
    setFilteredItems(values.selectedSpecificItems);
  }, [values.selectedSpecificItems]);

  useEffect(() => {
    setFilteredItems(selectedItems.filter(
      item => item.title.toLowerCase().includes(queryValue.toLowerCase())
    ) ?? []);
  }, [selectedItems, queryValue]);

  const rowsMarkup = filteredItems.slice(page * limit - limit, page * limit).map(
    (item, index) => {
      return (
        [
          page * limit - limit + index + 1,
          <div
            className={`${item.type === 'collection' ? 'cp' : ''}`}
            onClick={item.type === 'collection' ? () => handleClickToCollection(item) : () => { }}
          >
            <Stack wrap={false} alignment={"center"}>
              <Thumbnail
                source={getItemImage(item)}
                size="small"
                alt={item.title}
              />
              <Tooltip content={<span>{item.title}</span>}>
                <Text as="span" variant="bodyMd">
                  {cutTitle({ title: item.title, maxLength: 22 })}
                </Text>
              </Tooltip>
            </Stack>
          </div>,
          // getStockQuantityColumn(item),
          getProductsColumn(item),
          <Button icon={DeleteMinor} onClick={() => handleDelete(item.id)} />
        ]
      );
    }
  );

  const getProductsCount = useMemo(() => {
    if (totalCount) {
      return totalCount;
    }
    return filteredItems.reduce((acc, item) => {
      if (item.id.includes("Collection")) {
        acc += item.productsCount;
      } else {
        acc += 1;
      }
      return acc;
    }, 0);
  }, [filteredItems, totalCount]);

  return (
    <LegacyCard>
      <LegacyCard.Section>
        <Stack vertical>
          <AddItemsButtons
            handleAddProducts={handleAddProducts}
            handleAddCollections={handleAddCollections}
            initialSelectionIds={initialSelectionIds}
            handleRemoveProducts={handleRemoveProducts}
          />
          <Text as={"p"} variant={"bodyMd"}>
            {`${getProductsCount} products added`}
          </Text>
          <Filters
            queryPlaceholder={"Search"}
            queryValue={queryValue}
            filters={[]}
            onQueryChange={setQueryValue}
            onQueryClear={handleQueryValueRemove}
            onClearAll={handleClearAll}
          />
          <div style={{ maxHeight: "30rem", overflowY: "auto" }}>
            <TargetedProductsTable
              columnContentTypes={tableColumnContentTypes}
              headings={tableHeadings}
              rows={rowsMarkup}
            />
            {isLoadingProducts && (
              <div style={{ textAlign: "center", padding: "1rem" }}>
                <Spinner accessibilityLabel="Loading products" size="large" />
              </div>
            )}
            <Pagination
              onNext={handleNextPage}
              onPrevious={handlePrevPage}
              hasNext={totalCount ? page * limit < totalCount : (filteredItems.length / limit) > page}
              hasPrevious={page > 1}
              label={page}
            />
          </div>
        </Stack>
      </LegacyCard.Section>
    </LegacyCard>
  );
};

export default SectionWithTable;
